import type { ApiWallet } from './wallet';
import type { ChainKey } from 'src/blockchain';
import type { ApiDealStepEvent } from './dealStep';
import type { ApiRentalEscrowTerms } from './escrow';
import type { Currency, PayoutCurrency } from './listing';
import type { ApiMakeItem, ApiModelItem } from './common';
import type { Fuel, Gearbox, BodyType, CarAttributes } from './car';

/**
 * Frontend mirror of the rental read-API DTOs (Phase 2a, GAC-stacks/api#24).
 * View-only: the renter consumes `GET /buyer/rental-deals[/:id]`.
 * Source of truth: api `src/infrastructure/dto/rental/*` + `src/domain/types/rental/*`.
 */

/** Backend status of a rental deal (1:1 with api RentalDealStatus). */
export enum RentalDealStatus {
  // Deploy lifecycle (backend-driven, Phase 1b)
  CREATED = 'created',
  DEPLOY_INITIATED = 'deploy_initiated',
  DEPLOY_CONFIRMED = 'deploy_confirmed',
  DEPLOY_FAILED = 'deploy_failed',
  // On-chain-aligned (mirrored from contract flags, Phase 1d)
  INITIALIZED = 'initialized',
  PAID = 'paid',
  PICKED_UP = 'picked_up',
  READY_FOR_PICKUP = 'ready_for_pickup',
  ACTIVE = 'active',
  OVERDUE = 'overdue',
  /** v2.1: overdue with an empty deposit — nothing left to charge, the car is still out. */
  DEPOSIT_EXHAUSTED = 'deposit_exhausted',
  RETURNED = 'returned',
  DEFECTED = 'defected',
  DISPUTED = 'disputed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

/** Canonical phase derived from the 11 on-chain flags (api RentalContractStep). */
export enum RentalContractStep {
  WAITING_FOR_INITIALIZE = 'waiting-for-initialize',
  WAITING_FOR_PAYMENT = 'waiting-for-payment',
  WAITING_FOR_PICKUP = 'waiting-for-pickup',
  WAITING_FOR_READY_FOR_PICKUP = 'waiting-for-ready-for-pickup',
  WAITING_FOR_START = 'waiting-for-start',
  ACTIVE = 'active',
  OVERDUE = 'overdue',
  /** v2.1 only: past the end date with the deposit at zero (derived, not an on-chain flag). */
  DEPOSIT_EXHAUSTED = 'deposit-exhausted',
  IN_INSPECTION = 'in-inspection',
  DEFECTED = 'defected',
  DISPUTED = 'disputed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

/** Pickup mode of a rental deal. */
export enum RentalDealMode {
  ONSITE = 'onsite',
  DELIVERY = 'delivery',
}

/** Vehicle class of a rental listing. */
export enum VehicleClass {
  ECONOMY = 'economy',
  BUSINESS = 'business',
  PREMIUM = 'premium',
  EXCLUSIVE = 'exclusive',
}

/**
 * On-chain escrow state: 11 flags + amounts (base units, string) + timeline
 * (unix seconds / counters, string) + the derived FSM step. `null` until the
 * contract is deployed and first synced.
 */
export interface ApiRentalContractState {
  address: string;
  hash: string;
  dealId: string;
  step: RentalContractStep;

  /**
   * 1 = old hourly-penalty contract, 2 = v2.1 "rental-clock", 3 = v2.2 (overdue paid
   * THROUGH a moment instead of counted in days), 4 = the EVM generation (v3: a deposit
   * dispute is settled by two matching proposals instead of the platform's own ruling).
   * Absent on an older api.
   */
  contractVersion?: number;

  // Amounts (base units)
  depositAmount: string;
  rentalAmount: string;
  platformFeeAmount: string;
  hourlyPenaltyRate: string;
  /** v2.1+: daily rate charged per started overdue day — the 1–3 day band (null on v1 deals). */
  dailyOverdueRate?: string | null;
  paidAmount: string;
  remainingDeposit: string;
  totalPenaltyCharged: string;

  // Timeline (unix seconds / counters)
  rentalPeriod: string;
  rentalStart: string;
  rentalEnd: string;
  inspectionStart: string;
  extensionTotal: string;
  penaltyHoursCharged: string;
  /** v2.1 only: overdue days already charged from the deposit (null on v1 and v2.2 deals). */
  overdueDaysCharged?: string | null;

  /**
   * v2.2: unix seconds through which overdue time is fully paid. Days are prepaid, so it
   * can stand up to a day ahead of now. Null on older generations, absent on an older api.
   */
  overdueChargedThrough?: string | null;
  /**
   * v2.2: base units already paid toward the first day the deposit could not fully cover —
   * that day stays owed instead of being forgiven. Null/absent as above.
   */
  overdueCarryPaid?: string | null;
  /** v2.2: unix seconds after which pay() reverts on-chain. Null/absent as above. */
  payDeadline?: string | null;
  /** v2.2: unix seconds after which the car can no longer be accepted. Null/absent as above. */
  bookedEnd?: string | null;

  // CC4 pending extension request (seconds; '0' = none)
  pendingExtensionPeriod: string;

  // Defect/dispute claim (amount in base units, docHash reference)
  defectAmount: string;
  defectDocumentHash: string;

  /**
   * The deposit dispute, mirrored from the contract's `getDispute()` (contracts v3, onchain §3.2).
   *
   * A proposal is what the **landlord** would keep out of the remaining deposit; the renter gets
   * the rest. `null` means "has not proposed", which zero cannot express — proposing zero is a
   * real position ("the landlord keeps nothing"), and the contract carries a separate
   * `*HasProposed` flag for exactly that reason.
   *
   * All four are optional on the client: they exist only on the EVM generation of the contract
   * (`contractVersion` 4), and the two repos deploy separately, so a build of this app can meet an
   * api that predates them.
   */
  disputeStartedAt?: string;
  renterProposal?: string | null;
  rentierProposal?: string | null;
  platformProposal?: string | null;

  // State flags
  isInitialized: boolean;
  isPaid: boolean;
  isPickedUp: boolean;
  isReadyForPickup: boolean;
  isActive: boolean;
  isOverdue: boolean;
  isReturned: boolean;
  isInspection: boolean;
  isCompleted: boolean;
  isDefected: boolean;
  isDisputed: boolean;

  createdAt: Date;
  updatedAt: Date | null;
}

/** Rental listing snapshot carried on a deal (mirror of RentalListingFullItemDto). */
export interface ApiRentalListingFull extends CarAttributes {
  id: string;
  sellerId: string;
  makeId: string;
  modelId: string;
  vin: string | null;
  year: number | null;
  color: string | null;
  mileage: number | null;
  // Duration-banded pricing (rental-clock v2.1 §2): a booking is charged at the band its length
  // falls into. Optional on the client because api and app deploy separately — a listing served
  // by a pre-grid api has none. `dailyPrice` is the derived "from" price (the 1–3 day band).
  tier1DailyPrice?: number | null;
  tier2DailyPrice?: number | null;
  tier3DailyPrice?: number | null;
  tier4DailyPrice?: number | null;
  dailyPrice: number;
  currency: Currency;
  payoutCurrency: PayoutCurrency;
  depositAmount: number;
  vehicleClass: VehicleClass;
  description: string | null;
  images: string[];
  active: boolean;
  location: string | null;
  minRentalDays: number;
  maxRentalDays: number;
  createdAt: Date;
  updatedAt: Date | null;
  make: ApiMakeItem;
  model: ApiModelItem;
  seller: ApiWallet;
  /** Occupied booking windows (rental + 7-day inspection tail) that block new bookings. */
  occupiedRanges?: ApiRentalOccupiedRange[];
  /**
   * Present (and `true`) only when the car is busy right now — a running booking,
   * a dealer's manual block, or an open defect hold. Absent = free.
   */
  rentedNow?: boolean;
  /**
   * End of the current occupancy window, exclusive (= the first free moment), ISO;
   * `null` = open-ended defect/dispute hold. Sent alongside `rentedNow` only.
   */
  rentedUntil?: string | null;
  /**
   * Seats and the daily kilometre allowance (api#397). Optional here: an api that predates them
   * sends none, and a listing published before them answers `null` — the dealer has not said, and
   * the screen shows nothing. "No daily limit" is the flag, never a number.
   */
  seats?: number | null;
  dailyKmLimit?: number | null;
  dailyKmUnlimited?: boolean;
}

/** An occupied booking window of a listing (mirror of RentalOccupiedRangeDto). */
export interface ApiRentalOccupiedRange {
  /** Start of the occupied window (booking start). */
  startDate: string;
  /**
   * End of the occupied window, exclusive (booking end + inspection tail), or
   * `null` for an open-ended defect/dispute hold — occupied from `startDate`
   * onward until the dealer re-enables the listing.
   */
  endDate: string | null;
}

/** A rental deal for the renter view (mirror of RentalDealFullItemDto). */
export interface ApiRentalDealItem {
  id: string;
  rentalListingId: string;
  renterId: string;
  status: RentalDealStatus;
  mode: RentalDealMode;
  vehicleClass: VehicleClass;
  /** Deposit in default crypto units. */
  depositAmount: number;
  /** Total rental amount in default crypto units. */
  rentalAmount: number;
  /** Display bridge: dailyOverdueRate / 24 (pre-grid deals carry their original hourly rate). */
  hourlyPenaltyRate: number;
  /** v2.1: the 1-3 band daily price per started overdue day (null on pre-grid deals; absent on an older api). */
  dailyOverdueRate?: number | null;
  /**
   * 1 = old hourly-penalty contract, 2 = v2.1 "rental-clock", 3 = v2.2 (overdue paid
   * THROUGH a moment instead of counted in days), 4 = the EVM generation (v3: a deposit
   * dispute is settled by two matching proposals instead of the platform's own ruling).
   * Absent on an older api.
   */
  contractVersion?: number;
  /** Rental period in seconds. */
  rentalPeriod: number;
  cryptoCurrency: PayoutCurrency;
  startDate: string;
  endDate: string;
  /**
   * When the booking was created. The unpaid-booking countdown is derived from it: a Book
   * without Pay is cancelled by the platform cron 30 minutes later (spec §4). Optional
   * because api and app deploy separately — a pre-PR4 api does not declare it.
   */
  createdAt?: string;
  contractAddress: string | null;
  /**
   * Which network this deal settles on. api resolved it when the booking was created — the
   * factory, the settlement token and every participant's address were picked from it — so the
   * screens read it rather than deciding anything (contracts v3, UI §2).
   *
   * Optional for the usual reason: the repos deploy separately, and a row written before the
   * multichain track carries none. `resolveDealChain` treats its absence as the legacy default.
   */
  chainKey?: ChainKey;
  /**
   * When the renter may claim the remaining deposit alone — dispute start + 14 days; `null` while
   * no dispute runs. Computed by api so both cabinets count down to the same moment the contract
   * would accept (contracts v3 §4).
   */
  staleClaimAvailableAt?: string | null;
  /** Everything that happened to the deal, oldest first. Detail endpoint only — lists stay light. */
  stepEvents?: ApiDealStepEvent[];
  /**
   * The terms sheet the renter's own wallet creates the escrow contract from (api#380): answered
   * by `prepare-payment` and the detail while the booking awaits a contract the renter is to
   * create, null once the address is recorded and on the platform-created path. Optional for the
   * usual reason — the repos deploy apart.
   */
  escrowTerms?: ApiRentalEscrowTerms | null;
  listing: ApiRentalListingFull;
  renter: ApiWallet;
  rentalContractState: ApiRentalContractState | null;
}

export interface ApiRentalDealList {
  items: ApiRentalDealItem[];
  total: number;
}

/**
 * Available rental listings to browse/book (GET /buyer/rentals). The api pages them by `limit` and
 * `offset` like the sale feed, but the answer carries only `total` — no `limit`, no `offset`.
 */
export interface ApiRentalListingList {
  items: ApiRentalListingFull[];
  total: number;
}

/** Filters of `GET /buyer/rentals` the storefront uses (api `rental-listing-list-params.dto.ts`). */
export interface ApiRentalListingListParams {
  /** One dealer's rentals — the dealer page. */
  sellerId?: string;
  makeId?: string;
  modelId?: string;
  minDailyPrice?: number;
  maxDailyPrice?: number;
  /** Only cars of this body, with this gearbox, on this fuel — one value each. */
  bodyType?: BodyType;
  gearbox?: Gearbox;
  fuel?: Fuel;
  /** One catalog page: `CATALOG_PAGE_SIZE` cars from `offset` (`src/utils/catalogUrl.ts`). */
  limit?: number;
  offset?: number;
}

/** Payload for creating a rental deal (POST /buyer/rentals/:id/deal). */
export interface ApiCreateRentalDealPayload {
  listingId: string;
  startDate: string;
  endDate: string;
  mode: RentalDealMode;
}

/**
 * Server-quoted price of a booking (mirror of RentalBookingQuoteDto). Amounts are base-units
 * strings in the settlement token, computed with the escrow contract's own integer math — the
 * total here is the total the wallet is later asked to approve (spec §2).
 */
export interface ApiRentalBookingQuote {
  days: number;
  /** Duration band that priced the booking: 'tier1' (1–3 days) … 'tier4' (16+ days). */
  tier: string;
  /** The band's daily price, in the listing's currency. */
  dailyPrice: number;
  currency: Currency;
  cryptoCurrency: PayoutCurrency;
  rentalAmount: string;
  depositAmount: string;
  platformFeeAmount: string;
  totalRequired: string;
}

/**
 * What `topUpDeposit()` accepts right now (`GET /buyer/rental-deals/:id/top-up-quote`,
 * v2.2 §2.3). Base units, quoted by the contract — it rejects anything above `maxTopUp`,
 * so this is the only number the wallet may be asked to approve. The split explains where
 * the money goes: the debt straight to the dealer, the rest back into the deposit.
 */
export interface ApiRentalTopUpQuote {
  cryptoCurrency: string;
  maxTopUp: string;
  depositGap: string;
  outstandingOverdue: string;
}

/**
 * What an extension costs (`GET /buyer/rental-deals/:id/extension-quote?days=N`,
 * v2.2 §2.7): priced by the tier band the extension's OWN length falls into, not by a
 * pro-rata slice of the booking. Base units.
 */
export interface ApiRentalExtensionQuote {
  cryptoCurrency: string;
  days: number;
  periodSeconds: number;
  extraRental: string;
  extraFee: string;
  totalExtra: string;
  startsFrom: string;
  /**
   * Whether the car is free for those days at all (api occupancy-truth §4.5).
   * Optional: the two repos deploy separately, so a build of this app can meet an
   * api that predates the field — the modal then simply shows no verdict.
   */
  availability?: ApiRentalExtensionAvailability;
}

/** The calendar half of the extension answer: the contract prices the days, this says who else holds them. */
export interface ApiRentalExtensionAvailability {
  /** free = nothing in the way; blocked = another booking; warning = the dealer's own block. */
  status: 'free' | 'blocked' | 'warning';
  checkedFrom: string;
  checkedUntil: string;
  /** When the car is free again; null when nothing is in the way or the obstacle is open-ended. */
  blockedUntil: string | null;
  conflictingDealId: string | null;
  conflictingBlackoutId: string | null;
}
